<template>
    <div
        class="convo"
        @mousemove.once="handleMouseMove"
        @keydown.once="handleKeyDown"
    >
        <img v-if="trackingUrl" :src="trackingUrl" alt="" />
        <header v-if="!inApp" :class="initialisedRef ? '' : 'splash'">
            <img id="company-logo" :src="getCDNUrl(logoRef)" alt="" />
        </header>
        <div v-show="initialisedRef" ref="containerRef" class="container">
            <div v-for="(item, idx) in itemsWithReplies" :key="idx">
                <transition name="from-left">
                    <div
                        v-if="
                            item.status !== 'new' &&
                            item.type === 'Say' &&
                            item.cardType !== TYPE_REVIEW_LINK_BUTTON
                        "
                        :class="[
                            'message',
                            'agent-message',
                            { 'current-agent-message': shouldHighlight(idx) },
                        ]"
                    >
                        <div
                            :class="[
                                'bubble',
                                { 'bubble-typing': item.status === 'entering' },
                            ]"
                        >
                            <span
                                v-if="item.status === 'entering'"
                                data-test="agent-say-entering"
                            >
                                <span class="dot dot-1"></span>
                                <span class="dot dot-2"></span>
                                <span class="dot dot-3"></span>
                            </span>
                            <span v-else data-test="agent-say-text">
                                <ConversationMessage
                                    :csat-labels="props.csatLabels"
                                    :content="getItemText(item)"
                                    :interact="shouldInteract(idx, true)"
                                    @updatescore="updateScore"
                                ></ConversationMessage>
                            </span>
                        </div>
                        <img
                            v-if="shouldHighlight(idx)"
                            class="avatar"
                            :src="getCDNUrl(avatar)"
                            alt=""
                        />
                    </div>
                </transition>

                <div
                    v-if="
                        item.status !== 'new' && item.type === 'ReceiveOption'
                    "
                >
                    <transition name="slide-down">
                        <div
                            v-if="
                                item.reply === undefined || item.reply == null
                            "
                        >
                            <div
                                :class="{
                                    'option-input': true,
                                    nps: isNps(item),
                                    fivestar: isFivestar(item),
                                    entering: item.status === 'entering',
                                }"
                            >
                                <button
                                    v-for="{
                                        value,
                                        key,
                                        badgeId,
                                        isSelected,
                                    } of getOptions(item)"
                                    :key="value"
                                    :class="{
                                        'option-item': true,
                                        'option-item-selected': isSelected,
                                    }"
                                    :aria-label="value"
                                    role="option"
                                    :aria-selected="isSelected"
                                    @click="
                                        () =>
                                            onOptionClicked(
                                                item,
                                                key,
                                                value,
                                                badgeId,
                                                idx
                                            )
                                    "
                                    v-html="fillParams(value)"
                                />
                                <button
                                    v-if="isMultiSelect(item)"
                                    :class="[
                                        'multi-select-options-done-button',
                                        { disabled: !hasSelectedOptions(item) },
                                    ]"
                                    aria-label="Done selecting options"
                                    :aria-disabled="!hasSelectedOptions(item)"
                                    @click="() => onMultiSelectDone(item, idx)"
                                >
                                    Done
                                </button>
                                <div
                                    v-if="isNps(item)"
                                    class="asknicely_buttonbar_label"
                                >
                                    <div class="label_left">
                                        {{ labelLeft(item) }}
                                    </div>
                                    <div class="label_right">
                                        {{ labelRight(item) }}
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="isFivestar(item)"
                                class="asknicely_buttonbar_label_fivestar"
                            >
                                <div class="label_left">
                                    {{ labelLeft(item) }}
                                </div>
                                <div class="label_right">
                                    {{ labelRight(item) }}
                                </div>
                            </div>
                        </div>
                    </transition>
                    <transition name="from-right">
                        <div
                            v-if="item.link && item.reply === 'yes'"
                            :id="`calendly-frame-${idx}`"
                            class="calendly-frame"
                        ></div>
                        <div
                            v-else-if="item.reply !== undefined"
                            class="message user-message"
                        >
                            <div class="bubble">
                                {{ getItemText(item).trim() }}
                            </div>
                        </div>
                    </transition>
                </div>

                <div
                    v-if="
                        item.status !== 'new' &&
                        item.type === 'ReceiveText' &&
                        item.param !== TYPE_REVIEW_LINK_BUTTON
                    "
                >
                    <transition name="slide-down">
                        <ReceiveTextField
                            v-if="item.reply === undefined"
                            :key="item.param"
                            v-model="replyText"
                            :should-focus="!preview"
                            :border-class="textareaBorderClass"
                            @submit-reply-text="receiveTextInput(item)"
                        ></ReceiveTextField>
                    </transition>
                    <div
                        v-if="contactLookupInvalid(item)"
                        class="validation-message"
                    >
                        <span>{{ validationMessage }}</span>
                        <span>&nbsp;</span>
                    </div>
                    <transition name="from-right">
                        <div v-if="item.reply" class="message user-message">
                            <div class="bubble">
                                {{ getItemText(item).trim() }}
                            </div>
                        </div>
                    </transition>
                </div>
                <div v-if="item.param === TYPE_REVIEW_LINK_BUTTON">
                    <ReviewLinkButton
                        :item="item"
                        :comment="commentForReviewLink"
                        :slug="slugRef"
                        :finished="stateRef.finished"
                        @load="next"
                    />
                </div>
            </div>
            <transition name="fade">
                <div
                    v-if="
                        showFinishedReloadWarning && props.surveyLinkConfig.slug
                    "
                >
                    <div
                        class="message app-message"
                        @click="returnToSurveyLink"
                    >
                        <div class="bubble">Click here to reset survey</div>
                    </div>
                </div>
            </transition>
        </div>
        <transition name="fade">
            <div v-if="showNetError" class="network-error">&#183;</div>
        </transition>
        <footer>
            <a
                v-if="!getNobranding()"
                id="asknicely-logo"
                :href="getReferrerUrl()"
                target="_blank"
            >
                <img
                    class="powered-by-b"
                    src="../../assets/img/powered-by-asknicely.png"
                    alt="Powered by AskNicely"
                />
            </a>
            <span class="google">This site is protected by reCAPTCHA and the Google
                <a href="https://policies.google.com/privacy">Privacy Policy</a>
                and
                <a href="https://policies.google.com/terms">Terms of Service</a>
                apply.
            </span>
        </footer>
    </div>
</template>

<script lang="ts" setup>
import {
    computed,
    nextTick,
    onBeforeMount,
    onBeforeUnmount,
    ref,
    watch,
} from 'vue'
import type { Ref } from 'vue'
import snarkdown from 'snarkdown'
import {
    initInlineWidget,
    isCalendlyEvent,
    hasCalendly,
} from '@/utils/calendly'
import {
    sendReply,
    sendReplyDiscreetlyWithRetryOnNetworkFail,
    stillHere,
    finished,
    preloadYearInReviewFinishedPage,
} from '@/api/conversation'
import {
    ConversationSpec,
    ConversationItem,
    ConversationDisplayItem,
    Options,
    ReceiveText,
    ReceiveOption,
    OptionLabels,
    LABEL_LEFT_NPS_DEFAULT,
    LABEL_LEFT_FIVESTAR_DEFAULT,
    LABEL_RIGHT_NPS_DEFAULT,
    LABEL_RIGHT_FIVESTAR_DEFAULT,
    LOOKUP_EMAIL,
    LOOKUP_PHONE,
    TYPE_REVIEW_LINK_BUTTON,
} from '@/entities/conversation'
import {
    initialState,
    proceed,
    transitionDisplay,
    setValues,
    setLabels,
    State as ConversationState,
} from './ConversationState'
import { sleep } from '@/utils/async'
import { answerToNPS, answerToFiveScore, Rating } from '@/domain/NPS'
import { init as initConfetti } from '@/animations/confetti'

import ConversationMessage from './components/ConversationMessage.vue'
import ReceiveTextField from './components/ReceiveTextField.vue'
import { interpolateData } from '@/utils/string'
import qs from 'qs'
import { getCDNUrl } from '@/utils/cdn'
import { debounce, validateEmail, validatePhone } from '@/utils/function'
import { defaultCsatOptions } from '@/transforms/conversation'
import { shuffle } from '@/utils/array'
import { SurveyLinkConfig } from '@/entities/appstore/survey-links'
import ReviewLinkButton from '@/pages/conversation/components/ReviewLinkButton.vue'

declare global {
    interface Window {
        askNicelyInterface: {
            postMessage: (message: string) => void
        }
        ReactNativeWebView: {
            postMessage: (message: string) => void
        }
        webkit: {
            messageHandlers: {
                askNicelyInterface: {
                    postMessage: (message: string) => void
                }
                ReactNativeWebView: {
                    postMessage: (message: string) => void
                }
            }
        }
    }
}

declare const UIkit

/**
 * This survey can run in two contexts:
 *
 * From email link:
 *    - answer and rating are known at mount time
 *    - initial score is automatically sent to the server on load
 *    - any subsequent question that tries to submit the initial score will be ignored (not displayed)
 *
 * From inapp:
 *    - answer is unknown at load time
 *    - response to lead question sends answer to server
 *    - rating is derived from answer received clientside
 */

const STILL_HERE_SECONDS = 60
const AUTO_REFRESH_SECONDS = 10
const STORAGE_KEY = 'asknicely-conversation'
const RESET_EVENTS = [
    'click',
    'keydown',
    'mousedown',
    'mousemove',
    'scroll',
    'touchstart',
    'touchmove',
]

const calendlyEventWindowSizes = {
    event_type_viewed: '970px',
    date_and_time_selected: '620px',
    event_scheduled: '490px',
}

interface Params {
    [key: string]: string
}

interface SavedData {
    state: ConversationState
    lastSaidText: string
    initialised: boolean
}

const receiveInput = async (
    item: ReceiveOption | ReceiveText,
    value: string,
    label: string,
    badgeId?: string,
    index?: number,
    multipleValues?: boolean
) => {
    createReply(item.param, value, label, item.type, badgeId, multipleValues)

    if (value === 'yes' && item.link) {
        nextTick(() => {
            loadCalendlyFrame(item.link, 'calendly-frame-' + index)
        })
    } else {
        await next()
    }
}

const emit = defineEmits(['updatespec'])
const debouncedReceiveInput = debounce(receiveInput, 500, true)

const containerRef = ref()

// expected in params
const answerRef: Ref<number | null> = ref(-1)
const firstInteractiveIndex = ref(-1)
const slugRef = ref('')
const logoRef = ref('')
const templateRef = ref('default')
const versionRef = ref(1)
const isFiveScoreRef = ref(false)
const inApp = ref(false)
const currentCalendlyFrame = ref()
const disableConfettiRef = ref(false)
const showFinishedReloadWarning = ref(false)
const refreshSurveyTimer: Ref<NodeJS.Timeout | null> = ref(null)

// transient
const replyText = ref('')
const lastSaidTextRef = ref('')
const answerCount = ref(0)

// persisted to localStorage
const stateRef: Ref<ConversationState> = ref({
    cursor: [],
    display: [],
    labels: {},
    spec: [],
    values: {},
})
const avatar = ref('')
const initialisedRef = ref(false) // has the splash screen been shown?
const showNetError = ref(false) // has there been a network error?
const errorTimeout = ref()

// browser tracking
const hasMouse = ref(false)
const hasKeyBoard = ref(false)
const trackingUrl = ref('')
const cardOptionLabels: Ref<OptionLabels> = ref({})

const validationMessage = ref('')
const textareaBorderClass = ref('grey')

const commentForReviewLink = ref('')

const computedRating = computed(() => {
    const answer = Number(answerRef.value)
    return isFiveScoreRef.value
        ? answerToFiveScore(answer)
        : answerToNPS(answer)
})

const storageKey = computed(() => {
    return `${STORAGE_KEY}/${slugRef.value}/${templateRef.value}/${versionRef.value}`
})

const itemsWithReplies = computed(() => {
    return stateRef.value.display.map((item, index) => {
        if (item.cardType !== 'message' && firstInteractiveIndex.value < 0) {
            firstInteractiveIndex.value = index
        }
        if (item.status !== 'new' && item.type === 'Say') {
            /* We need to transfer label text from the card to a variable as the 'Received Option' card, where the labels
               are displayed, don't have this data. */
            if (item.optionLabels) {
                cardOptionLabels.value = {
                    labelLeft: item.optionLabels?.labelLeft,
                    labelRight: item.optionLabels?.labelRight,
                }
            }
        }
        // Randomize order of options, if random is enabled and item is new
        if (
            item.status === 'new' &&
            item.type === 'ReceiveOption' &&
            item.options.length &&
            item.randomizeOrder
        ) {
            shuffle(item.options)
        }
        return {
            ...item,
            reply: getItemReply(item),
        }
    })
})

const labelLeft = (item) => {
    if (isNps(item)) {
        return cardOptionLabels.value.labelLeft
            ? cardOptionLabels.value.labelLeft
            : getDefaultLeftLabel(props.params.labelLeft)
    }

    if (isFivestar(item)) {
        return props.params.labelLeft
            ? props.params.labelLeft
            : LABEL_LEFT_FIVESTAR_DEFAULT
    }

    return null
}

const labelRight = (item) => {
    if (isNps(item)) {
        return cardOptionLabels.value.labelRight
            ? cardOptionLabels.value.labelRight
            : getDefaultRightLabel(props.params.labelRight)
    }

    if (isFivestar(item)) {
        return props.params.labelRight
            ? props.params.labelRight
            : LABEL_RIGHT_FIVESTAR_DEFAULT
    }

    return null
}

const getDefaultRightLabel = (rightLabel: string | null) => {
    return rightLabel ? rightLabel : LABEL_RIGHT_NPS_DEFAULT
}

const getDefaultLeftLabel = (leftLabel: string | null) => {
    return leftLabel ? leftLabel : LABEL_LEFT_NPS_DEFAULT
}

interface Props {
    preview?: boolean
    params: Params
    spec: ConversationSpec
    browserTracking: boolean
    surveyLinkConfig: SurveyLinkConfig
    csatLabels: Options
}

const props = withDefaults(defineProps<Props>(), {
    csatLabels: () => defaultCsatOptions(),
})

watch(
    () => props.spec,
    async () => {
        stateRef.value = await initialState(props.spec, props.params) // Reset everything as the conversation has changed
        next()
    }
)

onBeforeUnmount(() => {
    RESET_EVENTS.forEach((rev) =>
        document.removeEventListener(rev, resetTimeout)
    )
})

onBeforeMount(async () => {
    stateRef.value = await initialState([], {})
    await initialiseState()
})

const initialiseState = async () => {
    await load()

    if (!initialisedRef.value) {
        await sleep(1000)
        initialisedRef.value = true
    }

    if (inApp.value) {
        postToParent({
            key: 'conversation_loaded',
        })
    }

    await transition()

    // notify server user is still present
    if (!props.preview) {
        const stillHereInterval = setInterval(() => {
            if (!stateRef.value.finished) {
                stillHere(slugRef.value, STILL_HERE_SECONDS)
            } else {
                clearInterval(stillHereInterval)
            }
        }, STILL_HERE_SECONDS * 1000)

        setupAutoRefresh()
    }

    // handle any calendly completed
    handleCalendlyMessages()

    // send initial score, if not done already and not inApp
    if (
        !stateRef.value.finished &&
        stateRef.value.values.initial_score === undefined &&
        !inApp.value
    ) {
        createReply(
            'initial_score',
            String(answerRef.value),
            String(answerRef.value),
            'ReceiveText'
        )
    }

    // if not already awaiting input on load, move ahead (only when it's not finished)
    if (
        !stateRef.value.finished &&
        (!stateRef.value.node ||
            stateRef.value.node.type.indexOf('Receive') === -1)
    ) {
        await next()
    }

    RESET_EVENTS.forEach((rev) => document.addEventListener(rev, resetTimeout))

    resetTimeout()
}

const isOptionSelected = (item, key) => {
    return item.options.find((option) => option.key === key)?.isSelected
}

const onOptionClicked = (
    item,
    value: string,
    label: string,
    badgeId?: string,
    index?: number
) => {
    if (isMultiSelect(item)) {
        const selectOptionIndex = item.options.findIndex(
            (option) => option.key === value
        )
        const selectOption = item.options[selectOptionIndex]

        selectOption.isSelected =
            'isSelected' in selectOption ? !selectOption.isSelected : true

        // item.options are nested object, so to force reactive, we need to use splice function to replace option
        item.options.splice(selectOptionIndex, 1, selectOption)

        return
    }
    debounce(receiveInput(item, value, label, badgeId, index), 500, true)
}

const hasSelectedOptions = (item) => {
    return item.options.some((option) => option.isSelected)
}

const onMultiSelectDone = (item, index) => {
    const selectedOptions = item.options.filter((option) => option.isSelected)
    if (selectedOptions.length > 0) {
        const values: string[] = [],
            labels: string[] = [],
            badgeIds: string[] = []

        for (const selectedOption of selectedOptions) {
            values.push(selectedOption.key)
            labels.push(selectedOption.value)
            badgeIds.push(selectedOption.badgeId)
        }

        debounce(
            receiveInput(
                item,
                values.join('|'),
                labels.join(','),
                badgeIds.join(','),
                index,
                true
            ),
            500,
            true
        )
    }
}

const receiveTextInput = async (item: ReceiveText) => {
    if (replyText.value.trim() === '') {
        return
    }

    // put contact_lookup validation here
    validationMessage.value = ''
    textareaBorderClass.value = 'grey'
    if (item.extraOption) {
        replyText.value = replyText.value.trim()
        if (
            item.extraOption === LOOKUP_EMAIL &&
            !validateEmail(replyText.value)
        ) {
            validationMessage.value = 'Invalid email'
            textareaBorderClass.value = 'red'
            return
        }
        if (
            item.extraOption === LOOKUP_PHONE &&
            !validatePhone(replyText.value)
        ) {
            validationMessage.value = 'Invalid phone number'
            textareaBorderClass.value = 'red'
            return
        }
    }

    if (item.param === 'comment') {
        commentForReviewLink.value = replyText.value
    }

    await debouncedReceiveInput(item, replyText.value, replyText.value)
}

const getItemText = (item: ConversationDisplayItem): string => {
    let key
    let val
    let reply

    switch (item.type) {
        case 'Say':
            return fillParams(item.value)

        case 'ReceiveText':
            key = item.param
            val = stateRef.value.values[key]
            return val || ''

        case 'ReceiveOption':
            key = item.param
            if (isMultiSelect(item)) {
                val = stateRef.value.values[key]?.split('|') ?? []
                const selectedOptions = item.options
                    .filter((opt) => val.includes(opt.key)) // Filter selected value
                    .map((opt) => opt.value) // Get label column values
                return fillParams(
                    selectedOptions.length > 0 ? selectedOptions.join(', ') : ''
                )
            }
            val = stateRef.value.values[key]
            reply = val && item.options.find((opt) => opt.key === val)
            return fillParams(reply ? reply.value : '')
    }
    return ''
}

const getItemReply = (item: ConversationItem): string | undefined => {
    if (item.type === 'ReceiveOption' || item.type === 'ReceiveText') {
        return stateRef.value.values[item.param]
    }
    return undefined
}

const getOptions = (item: ReceiveOption) => {
    let opts = item.options.map(({ key, value, badgeId, isSelected }) => ({
        key,
        value: fillParams(value) || '0', // because smarty json_encode parses '0' as ''
        badgeId,
        isSelected,
    }))
    return opts
}

// highlight any text before an active Receive, or one with no following item
const shouldHighlight = (idx: number) => {
    const { display } = stateRef.value
    const item = display[idx]
    const following = display[idx + 1]
    const states = ['active', 'entering', 'leaving']

    return (
        (item && item.status && states.indexOf(item.status) > -1) ||
        (following
            ? following.type.indexOf('Receive') === 0 &&
              following.status &&
              states.indexOf(following.status) > -1
            : true)
    )
}

// allow interaction with any widgets before an active Receive
const shouldInteract = (idx: number, firstOnly = false) => {
    const { display } = stateRef.value
    const following = display[idx + 1]

    let firstInteractive = firstInteractiveIndex.value
    if (!firstOnly) {
        firstInteractive = idx
    }

    return following
        ? following.type.indexOf('Receive') === 0 &&
              following.status &&
              following.status !== 'done' &&
              idx === firstInteractive
        : false
}

const isNps = (item: { param?: string; cardType?: string }) => {
    return (
        (item.param && !!item.param.match(/_nps$/)) ||
        (item.cardType && item.cardType == 'nps')
    )
}

const isFivestar = (item: { param?: string; cardType?: string }) => {
    return (
        (item.param && !!item.param.match(/_fivestar$/)) ||
        (item.cardType && item.cardType == 'fivestar')
    )
}

const isMultiSelect = (item) => {
    return 'multiSelect' in item && item.multiSelect
}

const getReferrerUrl = () => {
    return props.params.referrerUrl
}

const getNobranding = () => {
    return props.params.nobranding
}

const loadCalendlyFrame = (link, elementId) => {
    if (!hasCalendly(props.spec)) {
        return
    }

    currentCalendlyFrame.value = document.getElementById(elementId)

    initInlineWidget(link + '?hide_event_type_details=1', elementId)
}

const handleCalendlyMessages = () => {
    if (!hasCalendly(props.spec)) {
        return
    }

    window.addEventListener('message', (e) => {
        if (isCalendlyEvent(e)) {
            currentCalendlyFrame.value.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
            const event = e.data.event.substring(9)
            if (calendlyEventWindowSizes[event] !== undefined) {
                currentCalendlyFrame.value.style.height =
                    calendlyEventWindowSizes[event]
            }
            if (e.data.event === 'calendly.event_scheduled') {
                next(true)
            }
        }
    })
}

const handleMouseMove = () => {
    hasMouse.value = true
    addTrackingImage()
}

const handleKeyDown = () => {
    hasKeyBoard.value = true
    addTrackingImage()
}

const addTrackingImage = () => {
    if (!props.browserTracking) {
        return false
    }
    const resolution = window.screen.width + '*' + window.screen.height

    const params: { [key: string]: any } = {}
    params.res = resolution
    if (hasMouse.value) {
        params.mouse = 1
    }
    if (hasKeyBoard.value) {
        params.keyboard = 1
    }

    trackingUrl.value = `/email/trackbrowser/${slugRef.value}?${qs.stringify(
        params
    )}`
}

const isLocalStorageAvailable = () => {
    try {
        localStorage.getItem(storageKey.value)
    } catch {
        return false
    }

    return true
}

const load = async () => {
    // parse initial data from template
    const spec = props.spec
    const params = props.params

    avatar.value = params.avatar
    const {
        slug,
        logo,
        answer,
        inapp,
        isFiveScore,
        template,
        version,
        disableConfetti,
    } = params
    slugRef.value = slug
    logoRef.value = logo
    templateRef.value = template
    versionRef.value = parseInt(version, 10)
    answerRef.value = /^\d+$/.test(answer) ? parseInt(answer, 10) : null
    isFiveScoreRef.value = !!isFiveScore
    inApp.value = !!inapp
    disableConfettiRef.value = !!disableConfetti
    answerCount.value = 0

    // try get data from local storage
    const saved =
        !props.preview &&
        isLocalStorageAvailable() &&
        localStorage.getItem(storageKey.value)

    if (saved) {
        const { state, lastSaidText, initialised } = JSON.parse(
            saved
        ) as SavedData

        if (
            answerRef.value &&
            !(parseInt(state?.values.answer) == answerRef.value)
        ) {
            updateScore({ score: answerRef.value }).then(() => {
                stateRef.value = state
                lastSaidTextRef.value = lastSaidText
                initialisedRef.value = initialised
            })
        } else {
            if (state?.values.conversationSlug) {
                // eslint-disable-next-line
                props.params.conversationSlug = state.values.conversationSlug
            }
            stateRef.value = state
            lastSaidTextRef.value = lastSaidText
            initialisedRef.value = initialised
        }

        // construct initial state manager and save
    } else {
        stateRef.value = await initialState(spec, params)
        const firstSayMessage = stateRef.value.display.find(
            (item) => item.type === 'Say'
        )

        if (firstSayMessage) {
            lastSaidTextRef.value = getItemText(firstSayMessage)
        }
        save()
    }
}

const save = () => {
    if (props.preview || !isLocalStorageAvailable()) {
        return
    }
    const data: SavedData = {
        state: stateRef.value,
        lastSaidText: lastSaidTextRef.value,
        initialised: initialisedRef.value,
    }
    localStorage.setItem(storageKey.value, JSON.stringify(data))
}

const createReply = (
    key: string,
    value: string,
    label: string,
    inputType: string,
    badgeId?: string,
    multipleValues?: boolean
) => {
    if (value.toString().trim() === '') {
        return
    }

    const newValues = { [key]: value }
    const newLabels = { [key]: label ?? value }

    // if in app and it's the lead question,
    // also load it into scope and derive rating
    // and notify parent window
    if (
        inApp.value &&
        isAnswerKey(key) &&
        !stateRef.value.values.hasOwnProperty('initial_score')
    ) {
        const answer = Number(value)
        answerRef.value = answer
        newValues.answer = value
        newValues.rating = computedRating.value
    }

    if (inApp.value && answerCount.value === 0) {
        postToParent({
            key: 'initial_score',
        })
    }
    answerCount.value++

    replyText.value = ''
    stateRef.value = setValues(stateRef.value, newValues) // set locally
    stateRef.value = setLabels(stateRef.value, newLabels)

    if (!props.preview) {
        sendReplyDiscreetlyWithRetryOnNetworkFail(slugRef.value, {
            // don't wait for response - UI is optimistic with no undo on fail
            key,
            value,
            badgeId,
            inputType,
            questionText: isAnswerKey(key) ? '' : lastSaidTextRef.value,
            template: templateRef.value,
            version: versionRef.value,
            answer: answerRef.value,
            multipleValues,
            conversationSlug: props.params.conversationSlug,
        }) // send to server
    }
    save()
}

const updateScore = async (data) => {
    const key = 'update_score'
    const value = String(data.score)

    let response = {}
    let url = new URL(location.href)

    // Handle "preview" score changes. Since we do not have a database entry for preview,
    // we just reload the page with the new score change
    if (url.pathname.includes('email/conversation/preview')) {
        let path = location.href
        window.location.href = path.substr(0, path.lastIndexOf('/') + 1) + value // Replace old score with new score.
        return
    }

    try {
        response = await sendReply(slugRef.value, {
            // we just updated the initial score, wait for result
            key,
            value,
            badgeId: undefined,
            inputType: 'ReceiveText',
            questionText: isAnswerKey(key) ? '' : lastSaidTextRef.value,
            template: templateRef.value,
            version: versionRef.value,
            conversationSlug: props.params.conversationSlug,
        })
    } catch (error) {
        showNetError.value = true
        if (errorTimeout.value) {
            clearTimeout(errorTimeout.value)
        }
        errorTimeout.value = setTimeout(() => {
            showNetError.value = false
        }, 1000)

        return
    }

    answerRef.value = Number(value)

    const newValues = {
        [key]: value,
        answer: answerRef.value ? answerRef.value.toString() : '0',
        rating: isFiveScoreRef.value
            ? answerToFiveScore(answerRef.value)
            : answerToNPS(answerRef.value),
    }

    answerCount.value = 0

    save()

    emit('updatespec', { answer: newValues.answer, spec: response })

    stateRef.value = setValues(stateRef.value, newValues) // Getting the new values set
}

const alert = (message: string, status?: string) => {
    UIkit.notify(message, {
        status,
        timeout: 3000,
        pos: 'top-center',
    })
}

// List of question types that can be used for the inital score. (missing 5 star)
const isAnswerKey = (key: string) => {
    return (
        [
            'initial_score',
            'score_nps',
            'answer',
            'answer_nps',
            'answer_csat',
        ].indexOf(key) > -1
    )
}

const transition = async () => {
    const SECOND_BOX = 1
    for await (const next of transitionDisplay(stateRef.value)) {
        stateRef.value = next
    }

    if (
        stateRef.value.display[stateRef.value.display.length - 1] !==
            undefined &&
        stateRef.value.display[stateRef.value.display.length - 1].status ===
            'active' &&
        'getBoundingClientRect' in (containerRef.value as HTMLDivElement) &&
        (containerRef.value as HTMLDivElement).children.length >= 3
    ) {
        const children = (containerRef.value as HTMLDivElement).children
        const top = children[children.length - 2].getBoundingClientRect().top
        const bottom =
            children[children.length - 1].getBoundingClientRect().bottom
        const paddingY = 100 + (children.length > 3 ? 80 : 0)
        const height = bottom - top + paddingY
        postToParent({
            key: 'set_height',
            value: height,
        })
    }
    save()
}

const next = async (afterCalendly = false) => {
    // move the conversation forward
    stateRef.value = await proceed(stateRef.value)
    save()

    if (stateRef.value.finished) {
        scrollToCurrent(afterCalendly)

        if (!props.preview) {
            await finished(slugRef.value, props.params.conversationSlug)
        }

        await sleep(600)

        const showConfetti =
            !disableConfettiRef.value && computedRating.value === 'promoter'
        if (showConfetti) {
            await throwConfetti()
        } else {
            await sleep(600)
        }

        postToOS('askNicelyDone', 'Success')

        postToParent({
            key: 'finished',
            value: lastSaidTextRef.value,
            showConfetti,
        })

        setupAutoRefresh()

        return
    }

    await transition()

    const { node } = stateRef.value

    // if item is a 'say', immediately go to next. otherwise wait for user input (skip or reply)
    if (!node) {
        return
    } else if (node.type === 'Say') {
        lastSaidTextRef.value = getItemText(node as ConversationDisplayItem)
        await next(afterCalendly)
    } else {
        scrollToCurrent(afterCalendly)
        save()
    }
}

const resetTimeout = () => {
    if (props.surveyLinkConfig.autoRefresh && props.surveyLinkConfig.slug) {
        const interval = props.surveyLinkConfig.timeout ?? STILL_HERE_SECONDS
        if (refreshSurveyTimer.value) {
            clearTimeout(refreshSurveyTimer.value)
        }
        refreshSurveyTimer.value = setTimeout(() => {
            returnToSurveyLink()
        }, interval * 1000)
    }
}

const setupAutoRefresh = () => {
    if (
        stateRef.value.finished &&
        props.surveyLinkConfig.autoRefresh &&
        props.surveyLinkConfig.slug
    ) {
        showFinishedReloadWarning.value = true
        setTimeout(() => {
            returnToSurveyLink()
        }, AUTO_REFRESH_SECONDS * 1000)
    }
}

const returnToSurveyLink = () => {
    window.location.href = '/s/feedback/' + props.surveyLinkConfig.slug
}

// @todo skip()

const scrollToCurrent = (afterCalendly = false) => {
    const agentMessages = document.getElementsByClassName('agent-message')
    const prev = agentMessages[agentMessages.length - 2] // use prev to provide some offset
    if (prev && !afterCalendly && !isInapp() && !props.preview) {
        prev.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    if (prev && isInapp()) {
        const current = agentMessages[agentMessages.length - 1]
        setTimeout(
            () =>
                current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                }),
            250
        )
    }
}

const isInapp = () => {
    const parameters = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
    })

    return (
        parameters.hasOwnProperty('inapp') ||
        (parameters.template_name && parameters.template_name === 'inapp')
    )
}

const fillParams = (str: string): string => {
    const { values, labels } = stateRef.value
    const replaced = interpolateData(
        str,
        { ...values, ...labels },
        props.params.questionType,
        (value) => {
            return `<ReselectScore v-bind:score=${value} v-bind:questionType="'${props.params.questionType}'" v-bind:interact="interact" v-bind:csatLabels="csatLabels" @updatescore="updateScore"></ReselectScore>`
        },
        props.csatLabels
    )

    return snarkdown(replaced.replace(/\n/gi, '<br/>').trim()) // Replace New Lines and trim.
}

const throwConfetti = async () => {
    const W = window.outerWidth
    const H = window.outerHeight

    const canvas = document.createElement('canvas')
    document.body.appendChild(canvas)
    canvas.style.top = '100px'
    canvas.style.pointerEvents = 'none' // confentti should be non blocking and click through
    await initConfetti(30, canvas)
    document.body.removeChild(canvas)
}

const postToParent = (
    data: { key: string; value?: any; showConfetti?: boolean },
    targetOrigin = '*'
) => {
    window.parent.postMessage(data, targetOrigin)
}

const postToOS = (event, msg) => {
    let data = JSON.stringify({ event: event, msg: msg })
    if (window.webkit != undefined) {
        if (window.webkit.messageHandlers.askNicelyInterface != undefined) {
            window.webkit.messageHandlers.askNicelyInterface.postMessage(data)
        }
        if (window.webkit.messageHandlers.ReactNativeWebView != undefined) {
            window.webkit.messageHandlers.ReactNativeWebView.postMessage(data)
        }
    }
    if (window.askNicelyInterface != undefined) {
        window.askNicelyInterface.postMessage(data)
    }
    if (window.ReactNativeWebView != undefined) {
        window.ReactNativeWebView.postMessage(data)
    }
}

const contactLookupInvalid = (item): boolean => {
    return (
        validationMessage.value != '' &&
        item.extraOption &&
        [LOOKUP_EMAIL, LOOKUP_PHONE].includes(item.extraOption)
    )
}
</script>

<style lang="less">
@import '~@/styles/rain/colour.less';

@brandColor: #666;

canvas {
    position: fixed;
    bottom: 0px;
    left: 0px;
    right: 0px;
    z-index: 10;
}

a,
a:link,
a:hover,
a:active,
a:visited {
    color: #fff;
    font-weight: bold;
    font-weight: 500;
    text-decoration: underline;
}

.convo {
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-size: 20px;
    position: relative;
}

.container {
    max-width: 600px;
    margin: 0px auto;
    padding: 20px 5px 110px 5px;
    position: relative;
}

.message {
    position: relative;
    pointer-events: none;
    .bubble {
        padding: 12px 20px;
        pointer-events: auto;
        border-radius: 20px;
        position: relative;
        float: left;
        transition: background-color 0.3s;
        .created-time {
            position: absolute;
            left: -58px;
            top: 5px;
            font-size: 0.75em;
            color: rgb(139, 152, 162);
        }
    }
    &:after {
        content: '';
        display: table;
        clear: both;
    }
    clear: both;
    padding: 7px;
    transition: all 0.3s;
}

@keyframes jump {
    0% {
        transform: translateY(0px);
    }
    25% {
        transform: translateY(-10px);
    }
    50% {
        transform: translateY(0px);
    }
    100% {
        transform: translateY(0px);
    }
}

.message.agent-message {
    .bubble {
        background-color: #fff;
        border: 1px solid @brandColor;
        border-radius: 0px 20px 20px 20px;
        margin-left: 20px;
        margin-right: 50px;
        position: relative;
        color: @brandColor;
        transition: background-color 0.2s;
        pointer-events: auto;
    }
    .bubble.bubble-typing {
        border: none;
        background-color: #eee;
        width: 120px;
        .dot {
            width: 10px;
            height: 10px;
            background: #fff;
            display: inline-block;
            border-radius: 10px;
            margin: 0px 5px;
        }
        .dot-1 {
            animation: jump 1.5s ease-in-out 0s infinite;
        }
        .dot-2 {
            animation: jump 1.5s ease-in-out 250ms infinite;
        }
        .dot-3 {
            animation: jump 1.5s ease-in-out 500ms infinite;
        }
        &:before {
            border-color: transparent #eee transparent transparent;
            top: 0px;
        }
        &:after {
            display: none;
        }
    }
}

.message.current-agent-message {
    .avatar {
        width: 50px;
        height: 50px;
        background: #ebebeb;
        border: 3px solid white;
        border-radius: 25px;
        position: absolute;
        top: -23px;
        left: 5px;
    }
    .bubble {
        color: #fff;
        background: @brandColor;
        transition: background-color 0.2s;
    }
    .bubble:after {
        display: none;
        transition: border-color 0.2s;
    }
}

.message.user-message {
    .bubble {
        background-color: rgb(244, 244, 246);
        float: right;
        border-radius: 20px 0px 20px 20px;
        margin-left: 58px;
        white-space: pre-line;
    }
}

.message.app-message {
    cursor: pointer;
    padding-left: 26px;
    .bubble {
        background-color: @brandPurple50;
        border-radius: 20px;
        text-align: center;
        white-space: pre-line;
        width: 100%;
    }
}

.option-input {
    margin: 10px auto;
    text-align: center;
    position: relative;
    opacity: 1;
    transition: opacity 0.2s;
    display: flex;
    flex-direction: column;

    h5 {
        text-transform: uppercase;
        color: rgb(78, 103, 124);
        padding-bottom: 10px;
        margin: 0px;
        font-weight: normal;
    }
    &.entering {
        opacity: 0;
    }

    &.nps {
        text-align: center;
        width: 320px;
        margin: 0px auto;
        display: block;

        h5 {
            display: none;
        }
        .option-item {
            background: @brandColor;
            width: 44px;
            height: 44px;
            line-height: 44px;
            color: #fff;
            font-weight: bold;
            font-size: 1.125em;
            border-radius: 100%;
            text-align: center;
            display: inline-block;
            padding: 0px;
            margin: 5px 2px;
        }
    }

    &.fivestar {
        display: flex;
        justify-content: center;
        flex-direction: row;

        .option-item {
            font-weight: normal;
            border: 0;
            margin: 0;
            padding: 0;

            &:hover {
                font-weight: bold;
                background-color: transparent;
                color: @brandColor;

                &:before {
                    content: '★';
                }
            }

            &:before {
                content: '☆';
                display: block;
                font-size: 50px;
                line-height: 60px;
            }
        }
    }
}

.option-item {
    margin: 0px 50px 0px 58px;
    padding: 12px 20px;
    background: #fff;
    color: @brandColor;
    border: 1px solid @brandColor;
    font-size: 0.875em;
    font-weight: bold;
    margin-bottom: 8px;
    border-radius: 3px;
    cursor: pointer;
    &.option-item-selected {
        background: @brandColor;
        color: white;
    }
}

.multi-select-options-done-button {
    margin: 16px 50px 0px 58px;
    padding: 16px 24px;
    background: @secondaryLightBlue;
    color: @brandColor;
    font-size: 0.875em;
    font-weight: bold;
    border-radius: 4px;
    cursor: pointer;
    border: none;

    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

header {
    display: block;
    text-align: center;
    padding: 10px;
    transition: 0.3s padding;
    transition-timing-function: ease-in-out;
    img {
        max-width: 250px;
        display: inline-block;
    }
    &.splash {
        padding: 200px 10px 10px 10px;
        transition: 0.5s padding;
        transition-timing-function: ease-in-out;
    }
}

footer {
    position: fixed;
    bottom: 0px;
    left: 0px;
    width: 100%;
    align-items: center;
    display: flex;
    flex-direction: column;
    background-color: #fff;
    padding-top: 15px;
    padding-bottom: 15px;

    #asknicely-logo {
        height: 24px;
        img.powered-by-b {
            width: 173px;
            height: 24px;
        }
    }
    .google {
        text-align: center;
        font-size: 12px;
        padding-top: 30px;
        color: #bcbcbc;
        a {
            color: #bcbcbc;
        }
    }
}

.asknicely_buttonbar_label {
    display: none;
    width: 100%;
    font-size: 11px;
    font-family: 'Roboto', Helvetica, Arial, 'Lucida Grande', sans-serif;
    color: #c1c1c1;
}

.nps .asknicely_buttonbar_label {
    display: block;
}

.asknicely_buttonbar_label_fivestar {
    display: flex;
    width: 100%;
    font-size: 11px;
    font-family: 'Roboto', Helvetica, Arial, 'Lucida Grande', sans-serif;
    color: #c1c1c1;
    justify-content: space-around;
}

.validation-message {
    display: flex;
    justify-content: space-around;
    color: @errorRed;
    font-size: 12px;
    line-height: 18px;
    font-family: 'Roboto', Helvetica, Arial, 'Lucida Grande', sans-serif;
}

.label_left {
    float: left;
    margin-left: 45px;
}
.label_right {
    float: right;
    margin-right: 45px;
}

.from-left-enter-active,
.from-left-leave-active {
    transition: all 0.3s;
}
.from-left-enter-from,
.from-left-leave-to {
    transform: translateX(-50px);
    opacity: 0;
}

.from-right-enter-active,
.from-right-leave-active {
    transition: all 0.3s;
}
.from-right-enter-from,
.from-right-leave-to {
    transform: translateX(50px);
    opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
    max-height: 1000px;
    overflow-y: hidden;
    transition: all 0.3s;
}
.slide-down-enter-from,
.slide-down-leave-to {
    max-height: 0px;
    opacity: 0;
}

/* Desktop overrides */
@media only screen and (min-width: 600px) {
    footer {
        max-width: unset;
    }
    .convo {
        font-size: 16px;
    }
    .message {
        &.current-agent-message {
            .avatar {
                width: 40px;
                height: 40px;
                border: none;
                border-radius: 20px;
                top: 5px;
            }
        }
        &.agent-message {
            .bubble {
                margin-left: 60px;
            }
            .bubble:before {
                transition: border-color 0.2s;
                position: absolute;
                content: '';
                top: -1px;
                left: -15px;
                border-style: inset;
                transform: rotate(360deg);
                height: 0;
                border-style: solid;
                border-width: 0 15px 8px 0;
                border-color: transparent @brandColor transparent transparent;
                z-index: 1;
            }
            .bubble:after {
                transition: border-color 0.2s;
                position: absolute;
                content: '';
                top: 0px;
                left: -11px;
                border-style: inset;
                transform: rotate(360deg);
                height: 0;
                border-style: solid;
                border-width: 0 15px 8px 0;
                border-color: transparent #fff transparent transparent;
                z-index: 2;
            }
        }
        .bubble {
            font-size: 1em;
        }
    }
    .message.user-message {
        .bubble {
            margin-left: 120px;
        }
    }
    .message.app-message {
        padding-left: 66px;
    }
    .option-input {
        h5 {
            float: left;
            padding-left: 40px;
        }
    }
    .option-item {
        width: 280px;
        margin: 0px auto 8px;
        font-size: 1em;
        font-weight: normal;
    }
    .multi-select-options-done-button {
        width: 280px;
        margin: 16px auto 8px;
        font-size: 1em;
    }
}

/* Windows browsers typically have ~17px scrollbars, and our other ruleset kicks in at 600.  Rounding to 640 for wriggle room. */
@media only screen and (min-width: 640px) {
    /* Preventing scrollbar jank */
    body {
        margin-left: calc(100vw - 100%);
        margin-right: 0;
    }
}

* {
    box-sizing: border-box;
}

.calendly-frame {
    height: 480px;
}

.network-error {
    bottom: 5px;
    font-family: sans-serif;
    font-size: 1.25em;
    left: 10px;
    position: fixed;
    color: red;
}

.fade-enter-active {
    transition: all 0.3s ease;
}
.fade-leave-active {
    transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
